import { type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInstantSearch, useSortBy } from 'react-instantsearch';
import {
  type ColumnDef,
  type ColumnOrderState,
  type Row,
  type SortingState,
  type Updater,
  type VisibilityState,
  getCoreRowModel,
  getGroupedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  BookX,
  CopyX,
  FolderX,
  Loader2,
  Menu,
  RotateCcwIcon,
  ScanSearch,
  Search,
  Trash2,
  Trash2Icon,
  UploadIcon,
  Users,
} from 'lucide-react';
import { type UniqueIdentifier, useDndMonitor } from '@dnd-kit/core';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { type LucideIcon } from 'lucide-react';
import { arrayMove } from '@dnd-kit/sortable';
import {
  ColumnsVisibilitySubmenu,
  CreateSubmissionLinkMenuItem,
  DeleteNodeMenuItem,
  DownloadZipMenuItem,
  EmptyTrashMenuItem,
  NewAlbumMenuItem,
  NewFolderMenuItem,
  RenameNodeMenuItem,
  ShareAlbumMenuItem,
  SortByValueMenu,
  SortOrderMenu,
  ToggleViewMenu,
  UploadToAlbumMenuItem,
  UploadToFolderMenuItem,
  ZoomInMenuItem,
  ZoomOutMenuItem,
} from '@/components/menu-items/node-menu-items';
import {
  type PageSizes,
  type PaginationState,
  defaultPaginationIndex,
  defaultPaginationSize,
} from '@/types/pagination';
import { MenuCheckboxItem, MenuProvider, MenuSeparator, MenuSub, dropdownPrimitives } from '@/components/menu-renderer';
import { useReconcileSelectedAssetsWithLiveData } from '@/hooks/data/selection/useReconcileSelectedAssetsWithLiveData';
import { BrowseDataTableListLoadingSkeleton } from '@/components/data-table/data-table-list-loading-skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTriggerButton } from '@/components/ui/dropdown-menu';
import { useClearSelectionOnHiddenChildren } from '@/hooks/data/selection/useClearSelectionOnHiddenChildren';
import { DataTableGridLoadingSkeleton } from '@/components/data-table/data-table-grid-loading-skeleton';
import { useReconcileSelectionWithPage } from '@/hooks/data/selection/useReconcileSelectionWithPage';
import { useReconcileChildSelection } from '@/hooks/data/selection/useReconcileChildSelection';
import { DataTableGridListView } from '@/components/data-table/data-table-grid-list-view';
import { type TableSettings, useTableStore } from '@/hooks/data/datatable/useTableStore';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { ReorderAssetsCommand } from '@/hooks/commands/albums/ReorderAssetsCommand';
import { buildParentPath, isChildTreeNode, toSelectedNode } from '@/lib/child-node';
import { ToolbarFilterButton } from '@/components/toolbars/toolbar-filter-button';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { type Ancestors, DAMNodeType, NodeType, toNodeType } from '@/types/tree';
import { useTableSelection } from '@/hooks/data/datatable/useTableSelection';
import { ChildrenSection } from '@/components/browser/children-section';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { HomeLayoutComponent } from '@/components/home/home-component';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { buildColumns } from '@/components/data-table/build-columns';
import { useAlbumAssets } from '@/hooks/data/albums/useAlbumAssets';
import { useAssetRating } from '@/hooks/data/assets/useAssetRating';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { SelectionBar } from '@/components/toolbars/selection-bar';
import { useOverflowCollapse } from '@/hooks/useOverflowCollapse';
import { ProgressBar } from '@/components/toolbars/progress-bar';
import { type BackendError } from '@/hooks/useFormErrorHandler';
import { useNodeFavorite } from '@/hooks/data/useNodeFavorite';
import { useDataTablePagination } from '@/hooks/usePagination';
import { type Asset, type GetAssetsRoot } from '@/types/asset';
import { type ColumnConfig, ColumnType } from '@/types/column';
import { DropdownSelect } from '@/components/dropdown-select';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { FilterBar } from '@/components/toolbars/filter-bar';
import { useCommandContext } from '@/context/CommandContext';
import { useAccount } from '@/hooks/data/account/useAccount';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { defaultColumnsSettings } from '@/types/datatable';
import { EmptyLayout } from '@/components/empty-layout';
import { Toolbar } from '@/components/toolbars/toolbar';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { useTreeContext } from '@/context/TreeContext';
import { Separator } from '@/components/ui/separator';
import { cn, isSelectableAsset } from '@/lib/utils';
import { DraggableItems } from '@/types/dragDrop';
import { type EntityType } from '@/types/entity';
import { SortOrderValues } from '@/types/sort';
import { QueryKey } from '@/lib/queryKeys';
import { GalleryView } from '@/types';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';

// Re-exported so existing `import { ColumnConfig } from '.../gallery-layout'`
// sites keep working; the canonical definition now lives in types/column. (DM-2393)
export type { ColumnConfig } from '@/types/column';

export type SelectionEventProps = MouseEvent<HTMLElement> | undefined;

/**
 * The only member of the triggering event `handleDoubleClick` ever reads (to skip navigating when
 * the double-click landed on the row's own select checkbox). Deliberately not `MouseEvent` or
 * `SyntheticEvent`: `useEnterToOpenAsset` fires the same handler from a native `window` keydown
 * listener, so the type has to admit a plain DOM `KeyboardEvent` as well as React's synthetic
 * `onDoubleClick` event, and this is the actual overlap between the two.
 *
 * `type` is what tells `isSelectColumnDoubleClick` below apart by trigger: `'dblclick'` for a real
 * mouse double-click, `'keydown'` for useEnterToOpenAsset's dispatch.
 */
export type DoubleClickTriggerEvent = { target: EventTarget | null; type: string };

/**
 * True only for a genuine mouse double-click landing on the row's own select checkbox — two rapid
 * clicks there are about toggling the checkbox, not opening the asset. Enter (useEnterToOpenAsset,
 * `event.type` `'keydown'`) is a single deliberate keystroke with no such ambiguity, so it still
 * opens the asset even when the checkbox happens to hold focus — notably because Radix's Checkbox
 * calls `preventDefault()` on every Enter press itself (checkboxes only toggle on Space), which
 * would otherwise make Enter-while-focused-on-the-checkbox look like a double-click landing there.
 */
export const isSelectColumnDoubleClick = (event: DoubleClickTriggerEvent) => {
  const target = event.target as HTMLElement | null;
  return event.type === 'dblclick' && Boolean(target?.closest('[data-select-column="true"]'));
};

// Height of the in-flow selection bar (h-12). It shrinks the list viewport from
// the bottom, so any row whose bottom sits within this distance of the viewport
// bottom would be covered by the bar once it appears.
const SELECTION_BAR_HEIGHT_PX = 48;

// Window to wait before showing the bar when a click lands on a row the bar
// would cover. It must outlast a deliberate double-click so the second click
// still lands on the (unmoved) row and its native dblclick can navigate before
// the bar — and the reveal scroll — ever happen, while staying short enough that
// a single-click selection of a bottom row doesn't feel laggy.
const SELECTION_BAR_DOUBLE_CLICK_DELAY_MS = 250;

export type GalleryLayoutProps = {
  data?: Partial<GetAssetsRoot>;
  columns?: Array<ColumnDef<Asset>>;
  /**
   * TanStack `GroupingState` — the column ids to group rows by. Absent or empty leaves the
   * table exactly as it is for browse/trash/shared/search. Supplied by the caller rather than
   * derived here so the layout stays entity-agnostic: `pages/duplicates.tsx` is the only
   * consumer today and it groups by `checksum`. Pass a module-scope constant, not a literal —
   * a fresh array identity on every render re-runs the grouped row model. (DM-2311)
   */
  grouping?: Array<string>;
  isPending: boolean;
  isLoading?: boolean;
  emptyState?: ReactNode;
  onError?: BackendError | Error | null;
  /**
   * The error state's glyph and its Retry. Both belong to the caller: the query a page's list
   * comes from is the page's own, and the layout can only guess at it from the route. Default to
   * browse's — the folder/album glyph for whatever is selected, and an `AssetData` invalidation.
   * (DM-2311)
   */
  onRetry?: () => void;
  errorIcon?: LucideIcon;
  /**
   * How many assets the node holds in total, for `useSelection`'s "everything here is selected"
   * check. The caller's, because `pagination.count` does not always count assets — the duplicates
   * endpoint counts groups. A page whose count is not an asset count passes nothing. (DM-2311)
   */
  assetCount?: number;
  /**
   * Whether the caller's list query is in flight, so Retry can show that it did something. The
   * error state stays up across a retry — there is no skeleton to fall back to while `onError` is
   * still set — so without this the screen is identical before and after the click. (DM-2311)
   */
  isRetrying?: boolean;
  refine?: (value: number) => void;
  progressText?: string;
  progressPercent?: string;
  enableDnd: boolean;
  ancestors?: Ancestors;
  tableEntity: EntityType;
  tableSettings?: Partial<TableSettings>;
  pagination: PaginationState;
  pageSizeOptions: PageSizes;
};

export const GalleryLayout = ({
  data,
  columns,
  grouping,
  isLoading,
  isPending,
  emptyState,
  onError,
  onRetry,
  errorIcon,
  isRetrying,
  assetCount,
  refine,
  progressText,
  progressPercent,
  enableDnd,
  tableEntity,
  tableSettings,
  ancestors,
  pagination,
  pageSizeOptions,
}: GalleryLayoutProps) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const tCommon = useTranslations('common');
  const tEmptyStates = useTranslations('emptyStates');

  const [items, setItems] = useState<Array<Asset>>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { queryString, debouncedSearch, showFilterInput, setShowFilter } = useSearchQuery(tableEntity);
  const { ref: toolbarRef, collapsed } = useOverflowCollapse<HTMLDivElement>();

  const { selection, selectedAssets, setSelectedAssets, deselectAllItems, contextItemSelection } =
    useGallerySelection();
  const { setCurrentSelectedType, currentSelectedType, contextNodeSelection, setSelectedNodes } = useTreeContext();
  const { reorderAssetsMutation } = useAlbumAssets();
  const { isAllSelected, setIsAllSelected, setLocateAssetId, setNodeAssetsCount } = useBrowserStore();
  const thumbnailSize = useUserSettingsStore((s) => s.thumbnailSize);
  const galleryView = useUserSettingsStore((s) => s.galleryView);
  const showFoldersAndAlbums = useUserSettingsStore((s) => s.showFoldersAndAlbums);
  const { apply } = useCommandContext();
  const { isTrashPage, isHomePage, isSharesPage, isPublicSharePage, isBrowsePage, isDuplicatesPage, pathSlugs } =
    useCurrentPage();
  const { nodeId, assets, name, favorite, children } = data ?? {};
  const { getActiveUploadSession, setUploadFolderPath, setUploadModalOpen } = useUploadStore();
  const { uploadSessionId } = getActiveUploadSession();
  const setReusableTreeSelectedFolder = useTreeStore((state) => state.setReusableTreeSelectedFolder);
  const { setRating, isPending: isRatingPending } = useAssetRating();
  const { navigateAndReset } = useDamNavigation();
  const { toggleFavorite, isFavoritePending } = useNodeFavorite();
  const {
    profile: { indexes },
    guest,
  } = useAccount();

  const search = useIsSearching();
  const findNodePath = useTreeStore(useShallow((state) => state.findNodePath));
  const selectedNode = useTreeStore((state) => state.selectedNode);
  const isAlbumView = selectedNode?.type === DAMNodeType.Album;

  const images = assets?.assets;
  const rowCount = useMemo(() => assets?.pagination?.count, [assets]);
  const instance = useInstantSearch();

  // Keep sort-by mounted so dropdown open/close doesn't reset search sorting.
  const searchSortBy = useSortBy({ items: indexes });
  const isSearchingInTrash = instance.indexUiState.configure?.filters === 'trashed:true';
  const settings = useTableStore(
    (s) => s.settings[tableEntity] ?? tableSettings ?? defaultColumnsSettings[tableEntity],
  );
  const setPagination = useTableStore((s) => s.setPagination);
  const setSettings = useTableStore((s) => s.setSettings);
  const getTableSettings = useTableStore((s) => s.getTableSettings);

  const { sortValue, sortOrder } = getTableSettings(tableEntity);

  // Children render on browse pages only, never during search (DM-2517). Albums are always
  // leaf containers so this is naturally empty there.
  //
  // Filtered to containers: `TreeRootNode.type` is a validated DAMNodeType, so an asset child is
  // representable even though TreeController's child listing defaults to folders and albums. The
  // previous narrowing treated "not an album" as "a folder", which would have given an asset a
  // folder chip, a folder drop target and a New Folder / Upload to Folder context menu.
  // Hiding the band (DM-2518) doesn't change whether the folder has children, so this stays
  // populated either way — a folder with hidden subfolders still isn't empty.
  // `showFoldersAndAlbums` only controls what renders, at the two call sites below.
  const childNodes = useMemo(
    () => (isBrowsePage && !search ? (children ?? []).filter(isChildTreeNode) : []),
    [isBrowsePage, search, children],
  );

  // The browsed folder's own id and full name path, already on hand from `data`/`ancestors` — so
  // `ChildrenSection`'s upload items can use it directly instead of the sidebar tree's node cache,
  // which never holds a node shown only in that section (DM-2518 follow-up).
  const childrenParentPath = useMemo(() => buildParentPath(nodeId, name, ancestors), [nodeId, name, ancestors]);

  // Clears or refreshes a selected child when it is trashed, moved away or renamed. `childNodes`
  // above doesn't change when the visibility setting toggles, so this never fires from that.
  // Mounted here rather than in ChildrenSection, which unmounts itself when a folder has no
  // children left.
  useReconcileChildSelection(useMemo(() => childNodes.map(toSelectedNode), [childNodes]));

  // Hiding the band (DM-2518) is a visibility change, not an existence one, so it needs its own
  // hook — the reconciler above only fires when a child actually stops existing.
  useClearSelectionOnHiddenChildren(showFoldersAndAlbums);

  /*
   * Hold the children on the filter and sort the assets are actually showing, not the live ones.
   *
   * The children are filtered and sorted client-side, so they re-rendered the instant you typed a
   * character or changed the sort, while the asset list was still fetching. The two halves of the
   * view visibly settled at different moments — folders first, assets a beat later — which read as
   * the layout breaking rather than as results arriving.
   *
   * Keyed on the assets payload's identity rather than a fetching flag: the query keeps previous
   * data while it refetches, so this object only changes once new results have genuinely landed.
   * That makes it the exact moment both halves should flip together.
   *
   * State adjusted during render, which React supports and re-runs the render for — not a ref
   * mutated during render, which it does not. A ref write survives a render React discards, so an
   * interrupted render could pair the *new* filter with the still-committed *old* assets and produce
   * exactly the desync this exists to prevent. Storing `assets` alongside the params is what makes
   * the comparison self-contained, and repeating a render is a no-op because the payload matches.
   */
  const [settledChildParams, setSettledChildParams] = useState({ assets, queryString, sortValue, sortOrder });
  if (settledChildParams.assets !== assets) {
    setSettledChildParams({ assets, queryString, sortValue, sortOrder });
  }

  const { limit, pageIndex } = useDataTablePagination(tableEntity);

  const assetsLength = useMemo(() => {
    if (assets?.assets) {
      return assets.assets.length;
    } else {
      return 0;
    }
  }, [assets]);

  useEffect(() => {
    if (images) {
      setItems(images);
    }
  }, [images]);

  useReconcileSelectionWithPage(images);
  useReconcileSelectedAssetsWithLiveData(images);

  useEffect(() => {
    if (isAllSelected && assets?.assets) {
      // Select-all works on the data, so it has to skip rows a live broadcast has taken
      // out from under it - they are still on screen, but acting on them would fail.
      const selectable = assets.assets.filter(isSelectableAsset);
      setSelectedAssets(selectable);
      // TEMPORARY DOUBLE-WRITE of the older TreeContext.selectedNodes store - a follow-up PR
      // untangles the two. See TreeContext.selectedNodes.
      setSelectedNodes(selectable.map((asset) => ({ id: asset.id, name: asset.name, type: asset.type })));
    }
  }, [isAllSelected, setSelectedAssets, setIsAllSelected, setSelectedNodes, assets]);

  // Written straight through from the caller rather than read off `data`: only the page knows
  // whether its `pagination.count` counts assets. The duplicates endpoint paginates *groups*
  // (`SELECT count(DISTINCT checksum)`), so that page passes nothing and this clears instead —
  // `useSelection` compares the value against `selectedAssets.length` to decide "everything in
  // this node is selected", and a group count makes that fire on a partial selection. Clearing
  // also drops whatever the last browse page left behind. (DM-2311)
  useEffect(() => {
    setNodeAssetsCount(assetCount);
  }, [assetCount, setNodeAssetsCount]);

  useEffect(() => {
    deselectAllItems();
    setIsAllSelected(false);
  }, [nodeId, deselectAllItems, setIsAllSelected]);

  const reorderAssetsCommand = ReorderAssetsCommand(
    reorderAssetsMutation,
    selectedAssets.map((asset) => asset.id),
    selectedNode?.node?.id,
  );

  const handleRightClick = (asset: Asset) => {
    contextNodeSelection({ id: asset.nodeId, name: asset.name, type: asset.type });
    setCurrentSelectedType(
      asset.type === DAMNodeType.Album
        ? NodeType.Albums
        : asset.type === DAMNodeType.Folder
          ? NodeType.Folders
          : NodeType.Assets,
    );
    contextItemSelection(asset);
  };

  const dataIds = useMemo<Array<UniqueIdentifier>>(() => items?.map(({ id }) => id) || [], [items]);
  const canReorderAssets = enableDnd && isAlbumView && sortValue === SortOrderValues.MANUAL;

  const { handleSelection } = useTableSelection({ items });
  const selectionSourceRowIdRef = useRef<string | null>(null);
  const deferSelectionBarRef = useRef(false);
  const [showSelectionBar, setShowSelectionBar] = useState(false);

  // Decide up front whether showing the bar would cover the just-clicked row. Only rows within the
  // bar's height of the viewport bottom are at risk, regardless of page size or scroll position —
  // measure the clicked row against its scroll container rather than guessing from its index.
  // Shared by asset rows (`handleRowClick`) and child folder/album rows (`handleChildRowClick`,
  // DM-1894 applied to a second set of rows) — one measurement, not two mechanisms.
  const computeDeferSelectionBar = useCallback(
    (event?: SelectionEventProps) => {
      const rowElement = event?.currentTarget.closest<HTMLElement>('[data-row-id]') ?? null;
      const scrollElement = event?.currentTarget.closest<HTMLElement>('[data-selection-scroll]') ?? null;
      return (
        galleryView === GalleryView.LIST &&
        rowElement !== null &&
        scrollElement !== null &&
        scrollElement.getBoundingClientRect().bottom - rowElement.getBoundingClientRect().bottom <
          SELECTION_BAR_HEIGHT_PX
      );
    },
    [galleryView],
  );

  const handleRowClick = useCallback(
    (row: Row<Asset>, event?: SelectionEventProps) => {
      selectionSourceRowIdRef.current = row.original.id;
      deferSelectionBarRef.current = computeDeferSelectionBar(event);
      handleSelection(row, event);
    },
    [computeDeferSelectionBar, handleSelection],
  );

  // Child rows selected through `toggleNodeItemSelection` directly, never touching `handleRowClick`,
  // so the bar always showed instantly regardless of where the row sat — the same DM-1894 bug on a
  // new set of rows. `ChildrenSection` calls this before it toggles selection.
  const handleChildRowClick = useCallback(
    (childId: string, event?: SelectionEventProps) => {
      selectionSourceRowIdRef.current = childId;
      deferSelectionBarRef.current = computeDeferSelectionBar(event);
    },
    [computeDeferSelectionBar],
  );

  // A selected child folder/album shows the bar independent of the asset count/selection —
  // it's on no asset page at all.
  const isChildSelected = selection.kind === 'nodes';

  useEffect(() => {
    if (!isChildSelected && (selectedAssets.length === 0 || assetsLength === 0)) {
      selectionSourceRowIdRef.current = null;
      deferSelectionBarRef.current = false;
      setShowSelectionBar(false);
      return;
    }

    if (showSelectionBar) {
      return;
    }

    // A covered row defers both the bar and its reveal scroll past the
    // double-click window; every other selection shows the bar instantly.
    if (!deferSelectionBarRef.current) {
      setShowSelectionBar(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowSelectionBar(true);
    }, SELECTION_BAR_DOUBLE_CLICK_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [assetsLength, selectedAssets.length, isChildSelected, showSelectionBar]);

  const columnConfigs: Array<ColumnConfig<Asset>> = useMemo(
    () => [
      {
        type: ColumnType.DragHandle,
        hide: galleryView === GalleryView.TILE,
        opts: {
          galleryView,
        },
      },
      {
        type: ColumnType.Select,
        opts: {
          galleryView,
          onClick: (e: MouseEvent<HTMLElement>, row: Row<Asset>) => {
            e.stopPropagation();
            // force it to behave like command+click
            handleRowClick(row, Object.assign(e, { metaKey: true }));
          },
        },
      },
      {
        type: ColumnType.Thumbnail,
        opts: {
          meta: {
            cellClassName: 'p-0! relative',
          },
          galleryView,
          thumbnailSize,
          size: 75,
          getType: (row: Row<Asset>) => row.original.type,
          getOverlayIcon: (row: Row<Asset>) => (row.original.trashed ? Trash2 : undefined),
        },
      },
      {
        type: ColumnType.Text,
        // The list table is table-fixed so long names truncate; explicit sizes keep the
        // Name column dominant instead of splitting leftover space evenly. (DM-2393)
        opts: { id: 'name', accessorKey: 'name', label: 'Name', galleryView, isMainColumn: true, size: 320 },
      },
      {
        type: ColumnType.Type,
        opts: { accessorKey: 'type', label: 'Type', galleryView },
      },
      {
        type: ColumnType.Rating,
        opts: {
          accessorKey: 'rating',
          label: 'Rating',
          galleryView,
          isLoading: isRatingPending,
          onChange: (row: Row<Asset>, rating: number) => setRating(row.original.id, row.original.rating ?? 0, rating),
          readOnly: (row: Row<Asset>) => !row.getIsSelected(),
        },
      },
      {
        type: ColumnType.Format,
        hide: galleryView === GalleryView.TILE || search,
        opts: {
          id: 'format',
          accessorFn: (asset: Asset) => asset.mimeType?.split('/')[1] ?? '',
          label: 'Format',
          galleryView,
          // `application/postscript` covers both .ai and .eps, so the name breaks the tie. (DM-2564)
          fileNameFn: (asset: Asset) => asset.fileName ?? asset.originalName,
        },
      },
      {
        type: ColumnType.Dimensions,
        hide: isPublicSharePage,
        opts: { galleryView },
      },
      {
        type: ColumnType.Text,
        hide: search,
        opts: {
          id: 'fileName',
          accessorKey: 'fileName',
          label: 'Original File Name',
          galleryView,
          truncateHeaderValue: true,
          size: 160,
        },
      },
      {
        type: ColumnType.Size,
        hide: search,
        opts: { galleryView },
      },
      {
        type: ColumnType.Text,
        hide: !isAlbumView,
        opts: {
          id: 'parentFolderName',
          accessorKey: 'parentFolderName',
          label: 'Parent',
          galleryView,
          size: 120,
        },
      },
      {
        type: ColumnType.Text,
        opts: {
          id: 'nodeId',
          accessorKey: 'nodeId',
          label: 'Asset Id',
          galleryView,
          truncateHeaderValue: true,
          size: 120,
        },
      },
      {
        type: ColumnType.Text,
        opts: {
          id: 'slug',
          accessorKey: 'slug',
          label: 'Slug',
          galleryView,
          size: 120,
        },
      },
      {
        type: ColumnType.Text,
        hide: isAlbumView || search,
        opts: {
          id: 'description',
          accessorKey: 'description',
          label: 'Description',
          galleryView,
          size: 160,
        },
      },
      {
        type: ColumnType.CommentCount,
        opts: {
          accessorKey: 'commentCount',
          label: 'Comments',
          galleryView,
        },
      },
    ],
    [galleryView, thumbnailSize, isPublicSharePage, isAlbumView, search, handleRowClick],
  );

  const defaultColumns: Array<ColumnDef<Asset>> = useMemo(() => buildColumns(columnConfigs), [columnConfigs]);

  // Sync table rowSelection with the selectedAssets
  const rowSelection = useMemo(() => {
    const rowSelectionMap: Record<string, boolean> = {};

    selectedAssets.forEach(({ id }) => {
      rowSelectionMap[id] = true;
    });

    return rowSelectionMap;
  }, [selectedAssets]);

  const isDevelopment = process.env.NODE_ENV === 'development';

  const setDataTablePagination = (updater: Updater<PaginationState>) => {
    if (typeof updater !== 'function') {
      return;
    }
    const oldPageInfo = table.getState().pagination;
    const newPageInfo = updater(oldPageInfo);
    if (oldPageInfo.pageSize !== newPageInfo.pageSize) {
      newPageInfo.pageIndex = defaultPaginationIndex;
    }
    setLocateAssetId(undefined);
    setPagination(tableEntity, { pageSize: newPageInfo.pageSize, pageIndex: newPageInfo.pageIndex });
  };

  // Grouping is opt-in per caller (DM-2311). When it's off, `getGroupedRowModel` stays
  // undefined and react-table falls back to `getPreGroupedRowModel()` — i.e. the exact row
  // model every existing consumer has today, with no extra memo layer in the chain.
  const isGrouped = Boolean(grouping?.length);

  const tableCols = columns ?? defaultColumns;

  const table = useReactTable({
    data: items,
    columns: tableCols,
    getCoreRowModel: getCoreRowModel(),
    ...(isGrouped && { getGroupedRowModel: getGroupedRowModel() }),
    manualSorting: true,
    state: {
      columnVisibility: settings.columnVisibility,
      columnOrder: settings.columnOrder,
      sorting,
      rowSelection: rowSelection,
      pagination,
      ...(isGrouped && { grouping }),
    },
    rowCount: rowCount,
    enableRowSelection: true,
    enableMultiRowSelection: true,
    onPaginationChange: setDataTablePagination,
    manualPagination: true,
    onColumnVisibilityChange: (updater: Updater<VisibilityState>) =>
      setSettings(tableEntity, {
        columnVisibility: typeof updater === 'function' ? updater(settings.columnVisibility!) : updater,
      }),
    onColumnOrderChange: (updater: Updater<ColumnOrderState>) =>
      setSettings(tableEntity, {
        columnOrder: typeof updater === 'function' ? updater(settings.columnOrder!) : updater,
      }),
    onSortingChange: setSorting,
    getRowId: (row) => row.id,
    debugTable: isDevelopment,
    debugHeaders: isDevelopment,
    debugColumns: isDevelopment,
  });

  useDndMonitor({
    onDragEnd(event) {
      const { active, over } = event;
      // Reorder assets when manual sorting is enabled
      if (
        canReorderAssets &&
        over?.data.current?.accepts?.includes(active.data.current?.type) &&
        over?.data.current?.type === DraggableItems.ASSET &&
        active?.data?.current?.type === DraggableItems.ASSET &&
        active.id !== over.id
      ) {
        setItems((prevItems) => {
          // Used to update position on the backend.
          const oldPosition = active.data?.current?.asset?.position;
          const newPosition = over.data?.current?.asset?.position;

          // Used to update position on the frontend.
          const oldIndex = dataIds.indexOf(active.id);
          const newIndex = dataIds.indexOf(over.id);

          if (!oldPosition && !newPosition && !oldIndex && !newIndex) {
            return items;
          }

          reorderAssetsCommand.update({ position: newPosition, oldPosition });

          apply(reorderAssetsCommand);

          return arrayMove(prevItems, oldIndex, newIndex);
        });
      }
    },
  });

  // Resolved outside the effect so the effect can depend on the resulting type rather than on
  // `selectedNode` itself: that is a tree-store object whose identity changes on writes that leave
  // its type alone, and re-running the effect for those is pure noise.
  // No active node means nothing is being browsed, which the panel reads as folders.
  const activeNodeType = selectedNode ? toNodeType(selectedNode.type) : NodeType.Folders;

  // The panel/toolbar's currentSelectedType follows the selection: a selected child node when
  // there is one, otherwise the active tree node once nothing (neither assets nor a child) is
  // selected. `selectedAssets` alone can't tell the two apart — it's empty in both cases.
  useEffect(() => {
    if (selection.kind === 'nodes') {
      setCurrentSelectedType(toNodeType(selection.item.type));
    } else if (selectedAssets.length === 0 && !isAllSelected) {
      // Guarded on isAllSelected: select-all sets the type to Assets and empties the array for a
      // render before refilling it, and without the guard this fallback overwrites that type, so
      // the panel labels every asset in the folder as folders.
      setCurrentSelectedType(activeNodeType);
    }
  }, [selection, selectedAssets.length, isAllSelected, activeNodeType, setCurrentSelectedType]);

  const handleDoubleClick = (asset: Asset, event: DoubleClickTriggerEvent) => {
    if (isSelectColumnDoubleClick(event)) {
      return;
    }

    if (asset.type && (asset.type === DAMNodeType.Album || asset.type === DAMNodeType.Folder)) {
      const nodePath = asset.ancestors ? asset.ancestors.map((ancestor) => ancestor.slug).join('/') : '';
      void navigateAndReset(
        `/browse/${nodePath}/${asset.slug}`,
        {
          ...(queryString && { queryString }),
        },
        asset.type as unknown as EntityType,
      );
    } else {
      void router.push({
        pathname: `/asset/[id]`,
        query: {
          id: asset.id,
          pageIndex: router.query.pageIndex ?? (pageIndex === 0 ? 1 : pageIndex),
          pageSize:
            router.query.pageSize && router.query.pageIndex
              ? router.query.pageSize
              : limit
                ? limit
                : defaultPaginationSize,
          origin: router.pathname.split('/')[1] || '',
          ...(selectedNode?.type === DAMNodeType.Folder && { folderId: selectedNode.node.id }),
          ...(selectedNode?.type === DAMNodeType.Album && { albumId: selectedNode.node.id }),
          ...(selectedNode?.type === DAMNodeType.Album && { albumName: selectedNode.node.name }),
          ...(queryString && { queryString }),
          ...(router.query.filters ? { filters: router.query.filters } : {}),
          ...(router.query.query || router.query.query === '' ? { query: router.query.query } : {}),
          ...(router.query.searchFilters || router.query.searchFilters === ''
            ? { searchFilters: router.query.searchFilters }
            : {}),
        },
      });
    }
  };

  const navigateToUploads = () => {
    const pathArray = findNodePath(nodeId ?? '', 'name');
    const path = pathArray?.join('/');
    if (path) {
      setUploadFolderPath(uploadSessionId, `/${path}`);
      setReusableTreeSelectedFolder(nodeId);
      setUploadModalOpen(true);
    }
  };

  const breadCrumbActions = () =>
    nodeId && (
      <>
        {selectedNode?.type === DAMNodeType.Folder && (
          <>
            <NewFolderMenuItem node={{ id: selectedNode.node.id }} parentId={selectedNode.node.id} />
            <NewAlbumMenuItem node={{ id: selectedNode.node.id }} parentId={selectedNode.node.id} />
            <MenuSeparator />
            <UploadToFolderMenuItem folderId={selectedNode.node.id} />
            <CreateSubmissionLinkMenuItem node={{ id: selectedNode.node.id, name: selectedNode.node.name }} />
            <DownloadZipMenuItem
              nodeIds={[selectedNode.node.id]}
              name={selectedNode.node.name}
              sourceType={DAMNodeType.Folder}
            />
          </>
        )}
        {selectedNode?.type === DAMNodeType.Album && selectedNode?.node && (
          <>
            <ShareAlbumMenuItem node={{ id: selectedNode.node.id, name: selectedNode.node.name }} />
            <MenuSeparator />
            <UploadToAlbumMenuItem albumId={selectedNode.node.id} albumName={selectedNode.node.name} />
            <DownloadZipMenuItem
              nodeIds={[selectedNode.node.id]}
              name={selectedNode.node.name}
              sourceType={DAMNodeType.Album}
            />
          </>
        )}
        <MenuSeparator />
        <RenameNodeMenuItem node={{ id: nodeId, type: selectedNode?.type }} />
        {selectedNode && name && (
          <DeleteNodeMenuItem
            node={{
              id: selectedNode.node.id,
              node: {
                ...selectedNode.node,
                name: name,
              },
              type: selectedNode.type,
            }}
          />
        )}
      </>
    );

  const selectedNodeType = currentSelectedType === NodeType.Folders ? 'folder' : 'album';

  const renderLayout = () => {
    if (guest || isPublicSharePage || (!selectedNode && isHomePage && !search && !router?.query?.slug)) {
      return <HomeLayoutComponent isPending={isPending} />;
    }

    // Ahead of the loading branch: callers hold `isPending` true until their own `pageReady` flag
    // flips, which a failed query never does — so this state was unreachable on a cold load and the
    // page sat on the skeleton behind the global error toast. (DM-2311)
    if (onError) {
      return (
        <EmptyLayout
          icon={errorIcon ?? (selectedNodeType === 'album' ? BookX : FolderX)}
          title={tCommon('oopsSomethingWentWrong')}
          message={tCommon('pleaseTryAgainLater')}
          // Spinning, but never `disabled` — not on `isPending`, which stays true for as long as
          // this screen is up, and not on `isRetrying` either: the one control that can clear this
          // screen must not be disableable by a flag a caller might get wrong. React Query dedupes
          // concurrent fetches for the key, so an impatient second click costs nothing.
          onClick={onRetry ?? (() => queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] }))}
          buttonIcon={isRetrying ? Loader2 : RotateCcwIcon}
          buttonIconClassName={isRetrying ? 'animate-spin' : undefined}
          buttonText={tCommon('retry')}
        />
      );
    }

    // Small patch that should be fixed when the pagination will be handled properly. In the rare case where the user deletes the last asset in the page, show the loading screen.
    if (
      isPending ||
      isLoading ||
      (assets?.pagination &&
        assets?.pagination.offset === assets?.pagination.count &&
        assets?.pagination.offset !== 0 &&
        assets?.pagination.count !== 0)
    ) {
      return galleryView === GalleryView.TILE ? (
        <DataTableGridLoadingSkeleton />
      ) : (
        <BrowseDataTableListLoadingSkeleton />
      );
    }

    // Zero assets, whichever put them there — a typed filter or simply an empty folder. The two
    // read the trailing state below differently (noResults vs. the no-asset copy) but both route
    // through this one region rather than through the separate whole-page branch further down.
    //
    // Read off the settled filter, not the live one: navigating to a child folder clears the live
    // `queryString` (home.tsx closes the filter panel on every folder change) a render before
    // `assets` itself updates, since `assets` intentionally keeps showing the old folder's stale,
    // filtered-empty result via keepPreviousData until the new folder's data actually lands. Reading
    // the live queryString here made `noResults` go false on that stale data and flash the no-asset
    // copy instead of the still-accurate "No Results" one. `settledChildParams.queryString` moves in
    // lockstep with `assets` (same object-identity gate above), so it can't get ahead of it.
    const assetsEmpty = assets?.assets?.length === 0;
    const noResults = Boolean(settledChildParams.queryString) && assetsEmpty;

    // A folder/album with neither assets nor children still belongs in this region: the old
    // separate whole-page branch produced the identical full-height layout from a second code path.
    // Gated on there being no caller-supplied `emptyState` — that prop is how search/path-not-found/
    // no-folders and the trash/shared pages keep their own genuinely different empty states, and
    // this must not shadow any of them.
    const hasChildOrAssetContent = Boolean(assets?.assets?.length) || childNodes.length > 0;
    const routeEmptyThroughAssetsRegion =
      isBrowsePage && Boolean(selectedNode) && !emptyState && !hasChildOrAssetContent;

    if (hasChildOrAssetContent || routeEmptyThroughAssetsRegion) {
      // Just the column ids, in order. The children section derives its caption colSpan from the
      // count and each child row derives its own cell layout — including which cell holds the
      // folder/album icon — by looking the columns up by id.
      const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
      const childrenSection = childNodes.length > 0 && (
        <ChildrenSection
          // Keyed on the folder so the expander collapses on navigating away but survives
          // paging within the same folder.
          key={nodeId}
          nodes={childNodes}
          // showFoldersAndAlbums (DM-2518) — a folder with hidden children still isn't empty, so
          // this only tells ChildrenSection to skip its own band, not the empty-state check above.
          hidden={!showFoldersAndAlbums}
          galleryView={galleryView}
          // The settled values, so the children change in step with the asset list rather than a
          // beat ahead of it.
          sortValue={settledChildParams.sortValue}
          sortOrder={settledChildParams.sortOrder}
          queryString={settledChildParams.queryString}
          pathSlugs={pathSlugs ?? []}
          parentPath={childrenParentPath}
          visibleColumnIds={visibleColumnIds}
          onRowClick={handleChildRowClick}
          // The filter can match a child while matching no asset. The section needs to know so it
          // can stand down when nothing matched at all — the state itself renders below, outside
          // the table, because that is the only place it can be centred in the view.
          assetsNoResults={noResults}
        />
      );

      /*
       * The assets-empty trailing state, centred in what the user can see.
       *
       * Rendered through `renderTrailingEmptyState` rather than inside the children section: in list
       * view the section's own rows live in a `table-fixed` table wider than the viewport, so
       * anything centred in a spanning `<td>` centres on the table and lands off-screen, and
       * left-aligning it instead pinned it to the corner. The slot puts it outside the table, where
       * "centre" means the middle of the visible area in both views.
       *
       * Two statements, never both: a typed filter that matched no assets keeps "No Results", and an
       * unfiltered folder/album with zero assets gets the same no-asset copy the whole-page branch
       * below uses (that branch is only reachable now when nothing is selected at all).
       */
      const selectedNodeTypeTitle = currentSelectedType === NodeType.Folders ? 'Folder' : 'Album';
      const assetsEmptyState = assetsEmpty ? (
        <EmptyLayout
          icon={ScanSearch}
          title={
            noResults
              ? tEmptyStates('noResults.title')
              : tEmptyStates('emptyNode.title', { nodeType: selectedNodeTypeTitle })
          }
          message={
            noResults
              ? tEmptyStates('noResults.description', { entity: tableEntity })
              : tEmptyStates(
                  selectedNodeType === 'album' ? 'emptyNode.albumDescription' : 'emptyNode.folderDescription',
                )
          }
          containerClassName="bg-transparent"
          {...(!noResults && {
            onClick: selectedNode && currentSelectedType === NodeType.Folders ? navigateToUploads : undefined,
            buttonIcon: UploadIcon,
            buttonText: tEmptyStates('emptyNode.buttonLabel'),
          })}
        />
      ) : undefined;

      return (
        <>
          <DataTableGridListView
            parentNodeId={nodeId}
            galleryView={galleryView}
            table={table}
            data={assets?.assets ?? []}
            selectedAssets={selectedAssets}
            dataIds={dataIds}
            handleClick={handleRowClick}
            handleRightClick={handleRightClick}
            rowCount={rowCount}
            isLoading={isPending}
            {...(!isTrashPage && { handleDoubleClick })}
            isSearchingInTrash={isSearchingInTrash}
            enableDnd={enableDnd}
            canReorderAssets={canReorderAssets}
            selectionBarVisible={showSelectionBar}
            selectionSourceRowId={selectionSourceRowIdRef.current}
            hasLeadingContent={childNodes.length > 0}
            slots={{
              renderListLeadingRows: () => childrenSection,
              renderGridLeadingBlock: () => childrenSection,
              // A hairline between groups, never above the first — the top of the list is
              // already a boundary, so a rule there reads as a stray empty row. (DM-2311)
              ...(isGrouped && {
                renderListGroupRow: ({ index }) => (
                  <tr className={cn(index === 0 ? 'h-4' : 'h-10')}>
                    <td colSpan={table.getVisibleLeafColumns().length}>
                      {index === 0 ? null : <hr className="h-px border-0 bg-ui-bevel" />}
                    </td>
                  </tr>
                ),
                renderGridGroupHeader: ({ index }) =>
                  index === 0 ? null : <hr className="h-px max-h-px min-h-px w-full border-0 bg-ui-bevel" />,
              }),
              ...(assetsEmptyState && { renderTrailingEmptyState: () => assetsEmptyState }),
            }}
          />
          {showSelectionBar && (selectedAssets.length > 0 || isChildSelected) && (
            <SelectionBar
              rowCount={rowCount ?? 1}
              nodeName={data?.name ?? currentSelectedType ?? DAMNodeType.Folder}
              selectedAssets={selectedAssets}
              setSelectedAssets={setSelectedAssets}
            />
          )}
          {/* Paginates assets, so it only earns its place when there are assets. A folder holding
              nothing but subfolders reaches this branch on the children alone and used to show
              page-size and page-number controls for an empty asset list. */}
          {Boolean(assets?.assets?.length) && (
            <DataTablePagination
              table={table}
              refine={refine}
              grouped={isGrouped}
              className="px-3 py-2"
              pageSizeOptions={pageSizeOptions}
            />
          )}
        </>
      );
    }

    if (emptyState) {
      return emptyState;
    }

    const selectedNodeTypeTitle = currentSelectedType === NodeType.Folders ? 'Folder' : 'Album';

    return (
      <EmptyLayout
        icon={ScanSearch}
        title={
          noResults
            ? tEmptyStates('noResults.title')
            : selectedNode
              ? tEmptyStates('emptyNode.title', { nodeType: selectedNodeTypeTitle })
              : tEmptyStates('nothingSelected.title')
        }
        message={
          noResults
            ? tEmptyStates('noResults.description', { entity: tableEntity })
            : selectedNode
              ? tEmptyStates(
                  selectedNodeType === 'album' ? 'emptyNode.albumDescription' : 'emptyNode.folderDescription',
                )
              : tEmptyStates('nothingSelected.description')
        }
        {...(!noResults && {
          onClick: selectedNode && currentSelectedType === NodeType.Folders ? navigateToUploads : undefined,
          buttonIcon: UploadIcon,
          buttonText: tEmptyStates('emptyNode.buttonLabel'),
        })}
      />
    );
  };

  // Trash has no filter input; the filter isn't available while searching either.
  const canToggleFilter = !search && !isTrashPage;

  return (
    <div className="flex h-full flex-col space-y-px">
      {isHomePage && !selectedNode && !search ? null : (
        <Toolbar ref={toolbarRef} className="justify-between border-t border-t-toolbar-bevel">
          <Breadcrumbs
            {...(isTrashPage
              ? {
                  rootIcon: Trash2Icon,
                  title: tCommon('trash'),
                  actionMenu: () => <EmptyTrashMenuItem />,
                }
              : isSharesPage
                ? {
                    rootIcon: Users,
                    title: tCommon('shared'),
                  }
                : isDuplicatesPage
                  ? {
                      rootIcon: CopyX,
                      title: tCommon('duplicates'),
                    }
                  : search
                    ? { rootIcon: Search }
                    : nodeId &&
                      name && {
                        onRootClick: () => {
                          void navigateAndReset('/browse', {});
                        },
                        items: ancestors && [...ancestors].reverse(),
                        lastItem: { id: nodeId, name: name, favorite: favorite },
                        actionMenu: breadCrumbActions,
                        enableFavorite: true,
                        isFavoritePending: isFavoritePending,
                        favorite: favorite!,
                        onFavoriteToggle: () => {
                          if (selectedNode) {
                            toggleFavorite(nodeId, Boolean(favorite), selectedNode?.type);
                          }
                        },
                      })}
          />

          <div className="flex items-center">
            <Separator orientation="vertical" className="h-6 bg-ui-light" />

            {collapsed ? (
              <DropdownMenu>
                <DropdownMenuTriggerButton
                  variant="main-toolbar"
                  size="main-toolbar"
                  icon={Menu}
                  data-cy="toolbar-dropdown-menu-button"
                  tooltip={tCommon('moreOptions')}
                  className="mx-1"
                />
                <DropdownMenuContent>
                  <MenuProvider primitives={dropdownPrimitives}>
                    {canToggleFilter && (
                      <>
                        <MenuCheckboxItem
                          checked={showFilterInput}
                          onCheckedChange={(checked) => setShowFilter?.(checked)}
                        >
                          {tCommon('showFilter')}
                        </MenuCheckboxItem>
                        <MenuSeparator />
                      </>
                    )}

                    {/* The duplicates query takes no sort argument (useDuplicates.ts), so a
                        Sort control there would write store values nothing reads. (DM-2311) */}
                    {!isDuplicatesPage && (
                      <MenuSub label={tCommon('sort')}>
                        <SortByValueMenu tableEntity={tableEntity} searchSortBy={searchSortBy} />
                        {!search && (
                          <>
                            <MenuSeparator />
                            <SortOrderMenu tableEntity={tableEntity} />
                          </>
                        )}
                      </MenuSub>
                    )}

                    <MenuSub label={tCommon('view')}>
                      <ToggleViewMenu showFoldersAndAlbumsToggle={isBrowsePage && !isAlbumView} />
                      <MenuSeparator />
                      <ColumnsVisibilitySubmenu table={table} />
                      <MenuSeparator />
                      <ZoomInMenuItem />
                      <ZoomOutMenuItem />
                    </MenuSub>
                  </MenuProvider>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex flex-row items-center">
                {canToggleFilter && (
                  <>
                    <ToolbarFilterButton
                      showFilterInput={showFilterInput}
                      onClick={() => setShowFilter?.(!showFilterInput)}
                    />
                    <Separator orientation="vertical" className="h-6 bg-ui-light" />
                  </>
                )}
                {!isDuplicatesPage && (
                  <>
                    <DropdownSelect title={tCommon('sort')} menuClassName="w-54">
                      <SortByValueMenu tableEntity={tableEntity} searchSortBy={searchSortBy} />
                      {!search && (
                        <>
                          <MenuSeparator />
                          <SortOrderMenu tableEntity={tableEntity} />
                        </>
                      )}
                    </DropdownSelect>
                    <Separator orientation="vertical" className="h-6 bg-ui-light" />
                  </>
                )}
                <DropdownSelect title={tCommon('view')} menuClassName="w-64">
                  <ToggleViewMenu showFoldersAndAlbumsToggle={isBrowsePage && !isAlbumView} />
                  <MenuSeparator />
                  <ColumnsVisibilitySubmenu table={table} />
                  <MenuSeparator />
                  <ZoomInMenuItem />
                  <ZoomOutMenuItem />
                </DropdownSelect>
              </div>
            )}
            <Separator orientation="vertical" className="h-6 bg-ui-light" />
          </div>
        </Toolbar>
      )}
      <ProgressBar text={progressText} progress={progressPercent} />

      {showFilterInput && debouncedSearch && !search && (
        <FilterBar showFilterInput={showFilterInput} queryString={queryString} onChange={debouncedSearch} />
      )}

      {renderLayout()}
    </div>
  );
};
